import React, { useMemo, useRef, useState } from "react";
import {
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DateSelectionButton from "../../components/DateSelectionButton";
import useApi from "../../hooks/useApi";
import HoverRating from "../../components/HoverRating";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getContentLabel, getStatusOptions } from "../../utils/utils";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import StarIcon from "@mui/icons-material/Star";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import {
  deleteEntryFromCache,
  updateEntryInCache,
} from "../../utils/cacheUtils";
import { useAuth } from "../../context/authContext";
import {
  currentStandoutYear,
  useStandoutMutations,
  useStandoutStatus,
} from "../../hooks/useCustomQuery";
import { StandoutIcon } from "../../components/icons";
import ListMenu from "../../components/ListMenu";
import ConfirmActionDialog from "../../components/ConfirmActionDialog";
import { useDebounce } from "../../hooks/useDebounce";
import ReactionsEditorDialog from "../../components/ReactionEditor";
import NotesEditorDialog from "../../components/NotesEditor";
import VisibilityIcon from "../../components/VisibilityIcon";
import ShareIcon from "@mui/icons-material/Share";
import { shareLink } from "../../utils/share";
import { buildContentPath, SHARE_ORIGIN } from "../../utils/detailPaths";
import {
  CONTENT_INDENT,
  DATE_COLUMN,
  EntryRow,
  LINE_HEIGHT,
  NOTE_LINE_HEIGHT,
  ReactionChips,
  ROW_GAP,
  SEASON_GUTTER,
} from "./journalEntryLayout";

// MUI Rating's default filled-star gold, which the stars here use unthemed
const RATING_GOLD = "#faaf00";

// The "add X" pills (rating/reaction/note, and standout). Muted by default;
// `color` tints the text, icon and outline together.
function PromptChip({ label, onClick, color = "text.disabled", disabled }) {
  return (
    <Chip
      variant="outlined"
      icon={<AddIcon fontSize="small" color="inherit" />}
      label={label}
      size="small"
      clickable
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: "fit-content",
        fontSize: "0.75rem",
        height: 22,
        px: 0.5,
        color,
        borderColor: color,
      }}
    />
  );
}

export default function JournalEntry({
  contentType,
  contentId,
  existingEntry = {},
  onDelete,
  isMobile,
  isOwn,
  showPrompt,
  seasonNumber, // 0 for the series itself, else the season number — shown in a
  // fixed-width gutter when this entry is part of a merged series+season list
  // (see ContentJournal.js). Null outside that view.
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { patchEntry, deleteEntry } = useApi();
  const theme = useTheme();
  // Genuine viewport width, unlike isMobile (which is the modal's fullscreen
  // flag): the three prompt pills stop fitting on one line on the narrowest
  // phones, so the two longest labels shorten.
  const isNarrow = useMediaQuery("(max-width:375px)");
  const statusOptions = getStatusOptions(theme);
  const status = existingEntry.status;
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  // Single delete-confirmation gate. null = closed; otherwise the shape the
  // backend returned ({ willClearStatus, untracksLibrary }), which picks the
  // dialog's wording — see deleteEntryMutation. The backend decides both
  // whether a confirmation is needed at all and which single message covers
  // it, so there's only ever one dialog for one delete action.
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [activeEditor, setActiveEditor] = useState(null);
  // Sticky reveal for the rating control once a user opts into rating an
  // entry that had none — must not collapse back to the prompt pill if they
  // then clear the rating back to null.
  const [ratingRevealed, setRatingRevealed] = useState(false);
  // Set once the rating is changed here, so the standout pill only follows
  // a fresh rating — not an old one seen on reopening the entry
  const [ratingEdited, setRatingEdited] = useState(false);
  // Owned here rather than inside DateSelectionButton so the more menu can
  // open the picker as well as the date button itself.
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const readOnly = !isOwn;
  const pendingMenuActionRef = useRef(null);

  const [editedEntry, setEditedEntry] = useState({
    content_type: contentType,
    content_id: existingEntry.content_id,
    date: existingEntry.date,
    status: existingEntry.status,
    rating: existingEntry.rating,
    reactions: existingEntry.reactions ? existingEntry.reactions : [],
    notes: existingEntry.notes ? existingEntry.notes : "",
    visibility: existingEntry.visibility,
  });

  // Only finished entries carry a rating at all.
  const canRate = status === "finished";
  const hasRating = editedEntry.rating != null;
  // Rating control is visible once it has a value, or once the user has
  // explicitly revealed it via the prompt pill or the more menu.
  const showRatingInput = canRate && (hasRating || ratingRevealed);

  // Local values, so the standout pill appears as soon as the stars are set
  // rather than after the debounced save. Dates are "YYYY-MM-DD".
  const standoutEligible =
    showPrompt &&
    isOwn &&
    canRate &&
    ratingEdited &&
    editedEntry.rating >= 4 &&
    Number(editedEntry.date?.slice(0, 4)) === currentStandoutYear();
  // Fetched only for an eligible entry, to rule out series and existing
  // standouts; adding refetches it (["standouts"]), which hides the pill
  const { data: standoutStatus } = useStandoutStatus(existingEntry.content_id, {
    enabled: standoutEligible,
  });
  const { addStandout, isAdding: isAddingStandout } = useStandoutMutations();

  const moreMenuOptions = useMemo(() => {
    if (!isOwn) return [];
    if (activeEditor) return [];

    const options = [];

    // The date is editable by tapping it directly, but that isn't discoverable,
    // so it leads the menu — it's the entry's primary attribute.
    options.push({
      value: "edit_date",
      label: editedEntry.date ? "Edit date" : "Add date",
      icon: <CalendarTodayIcon />,
    });

    if (canRate && !showRatingInput) {
      options.push({
        value: "add_rating",
        label: "Add rating",
        icon: <StarIcon />,
      });
    }

    if (canRate) {
      options.push({
        value: "edit_reactions",
        label:
          editedEntry.reactions?.length > 0
            ? "Edit reactions"
            : "Add reactions",
        icon: <EmojiEmotionsIcon />,
      });
    }

    options.push({
      value: "edit_notes",
      label: editedEntry.notes ? "Edit note" : "Add note",
      icon: <EditIcon />,
    });
    options.push({
      value: "share",
      label: "Share entry",
      icon: <ShareIcon />,
    });
    options.push({
      value: "delete",
      label: "Delete entry",
      icon: <DeleteIcon />,
    });

    return options;
  }, [
    isOwn,
    canRate,
    showRatingInput,
    editedEntry.reactions?.length,
    editedEntry.notes,
    editedEntry.date,
    activeEditor,
  ]);

  const hasReactions = editedEntry.reactions?.length > 0;
  const hasNotes = Boolean(editedEntry.notes?.trim());

  const patchEntryMutation = useMutation({
    mutationFn: ({ entryId, patch }) => patchEntry(entryId, patch),
    onSuccess: (data) => {
      updateEntryInCache({
        queryClient,
        contentType,
        entryObject: data.entryObject,
        username: user.username,
      });
    },
  });

  const deleteEntryMutation = useMutation({
    mutationFn: ({ entryId, confirm }) => deleteEntry(entryId, confirm),
    onSuccess: (data, variables) => {
      // The backend decides whether a confirmation is needed at all (content
      // in the entry, a status about to clear/change, or both) and nothing
      // has been touched yet — surface it rather than proceed.
      if (data.requireConfirmation) {
        setDeleteConfirmation({
          willClearStatus: data.willClearStatus,
          untracksLibrary: data.untracksLibrary,
          hasContent: data.hasContent,
        });
        return;
      }
      deleteEntryFromCache({
        queryClient,
        contentId,
        contentType,
        deletedEntryId: variables.entryId,
        // Deleting the only finished entry also clears the status server-side,
        // which in turn recomputes (or, if nothing else anchors the show,
        // sweeps) the parent series — the backend reports both so the caches
        // don't hold a status the database no longer has.
        clearedContentIds: data.clearedContentIds,
        affectedContentIds: data.affectedContentIds,
        username: user.username,
      });
      setDeleteConfirmation(null);
      onDelete?.();
    },
  });

  const updateEditedEntry = (name, value) => {
    setEditedEntry((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveField = (patch) => {
    patchEntryMutation.mutate({
      entryId: existingEntry.journal_id,
      patch,
    });
  };

  /**
   * The "keep as finished" alternative to deleting. Strips what the user put
   * in the entry but keeps the row, which is what holds the finished status
   * up (see delete_entry). The result is the same bare entry create_status
   * writes when someone taps "finished", so this lands in a state the app
   * already produces rather than a special one.
   *
   * The date is deliberately kept: it records when the content was finished,
   * not something added to the entry, and is excluded from the backend's
   * has_content check for the same reason. An already-bare entry has nothing
   * to strip, so this is a no-op and the dialog just closes.
   */
  const keepAsFinished = () => {
    if (!deleteConfirmation?.hasContent) return;

    const clearedFields = { rating: null, reactions: [], notes: "" };
    setEditedEntry((prev) => ({ ...prev, ...clearedFields }));
    setRatingRevealed(false); // collapse the rating control back to its prompt
    saveField(clearedFields);
  };

  /**
   * Copy for the single delete confirmation. Which case applies is decided
   * server-side (see delete_entry); this only renders it.
   */
  const getDeleteConfirmationMessage = () => {
    if (!deleteConfirmation) return null;

    const { willClearStatus, untracksLibrary, hasContent } = deleteConfirmation;
    if (!willClearStatus) return "Deleting this entry cannot be undone.";

    const consequence = untracksLibrary
      ? `This is your only finished entry for this ${getContentLabel({
          type: contentType,
          lowercase: true,
        })}. Deleting it also clears its finished status and removes it from your library.`
      : "This is the only entry marking the whole series as finished. Deleting it will update the series status based on your individual seasons.";

    return hasContent
      ? `${consequence} Keeping it as finished clears this entry's contents instead.`
      : consequence;
  };

  const handleMoreMenuSelect = (value) => () => {
    pendingMenuActionRef.current = value;
    // menu logic is set via ListMenu transition props to ensure they only take
    // effect after the menu has closed (since actions can alter list options available)
    setMoreMenuAnchorEl(null);
  };

  const debouncedSaveRating = useDebounce((rating) => {
    patchEntryMutation.mutate({
      entryId: existingEntry.journal_id,
      patch: { rating },
    });
  }, 1000);

  const statusIcon = statusOptions[status].icon;

  const dateButton = (
    <DateSelectionButton
      value={editedEntry.date}
      setValue={(newValue) => {
        const newDate = newValue ? newValue.format("YYYY-MM-DD") : null;
        updateEditedEntry("date", newDate);
        saveField({ date: newDate });
      }}
      statusAction={statusOptions[status].label.toLowerCase()}
      disabled={readOnly}
      open={datePickerOpen}
      setOpen={setDatePickerOpen}
    />
  );

  // Desktop keeps icon and date together in the leading gutter; mobile splits
  // them so the icon occupies the gutter and the date starts where every
  // other row's content does.
  const dateBlock = (
    <Stack direction="row" alignItems="center" spacing={1}>
      {statusIcon}
      {dateButton}
    </Stack>
  );

  const ratingBlock = !showRatingInput ? null : (
    <HoverRating
      value={editedEntry.rating}
      onChange={(event, newValue) => {
        updateEditedEntry("rating", newValue);
        setRatingEdited(true);
        debouncedSaveRating(newValue);
      }}
      readOnly={readOnly}
    />
  );

  const showRatingPrompt = showPrompt && isOwn && canRate && !showRatingInput;
  const showReactionPrompt = showPrompt && isOwn && canRate && !hasReactions;
  const showNotesPrompt = showPrompt && isOwn && !hasNotes;
  // Shown once it's rated 4+ with a finish date in this year: the backend's
  // only standout rule is a finished entry dated in the year, so tapping adds
  // it outright, with none of StandoutAddFlow's date prompt. It replaces the
  // rating pill, so there are never more than three prompts.
  const showStandoutPrompt =
    standoutEligible &&
    // A series entry would need a season picked first; already a standout
    // needs nothing
    standoutStatus &&
    !standoutStatus.is_series &&
    standoutStatus.state !== "contains";

  const ratingPrompt = showRatingPrompt ? (
    <PromptChip
      label={isNarrow ? "rate" : "rating"}
      onClick={() => setRatingRevealed(true)}
    />
  ) : null;

  const reactionPrompt = showReactionPrompt ? (
    <PromptChip
      label={isNarrow ? "react" : "reaction"}
      onClick={() => setActiveEditor("reactions")}
    />
  ) : null;

  const notesPrompt = showNotesPrompt ? (
    <PromptChip label="note" onClick={() => setActiveEditor("notes")} />
  ) : null;

  const standoutPrompt = showStandoutPrompt ? (
    <PromptChip
      label={
        <Box
          component="span"
          // flex, not inline-flex: an inline-level box sits on the text
          // baseline, which lifted the icon and label above centre
          sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
        >
          <StandoutIcon sx={{ fontSize: 14 }} />
          standout
        </Box>
      }
      color={RATING_GOLD}
      disabled={isAddingStandout}
      onClick={() => addStandout({ contentId: existingEntry.content_id })}
    />
  ) : null;

  const moreButton = isOwn ? (
    <IconButton
      size="small"
      sx={{ p: 0.5 }}
      onClick={(event) => setMoreMenuAnchorEl(event.currentTarget)}
    >
      <MoreHorizIcon />
    </IconButton>
  ) : null;

  const reactionsBlock =
    canRate && hasReactions ? (
      <ReactionChips reactions={editedEntry.reactions} />
    ) : null;

  const notesBlock = hasNotes ? (
    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
      {editedEntry.notes}
    </Typography>
  ) : null;

  // Lives in the notes row's trailing gutter, directly under the more button.
  const visibilityButton = hasNotes ? (
    <VisibilityIcon
      value={editedEntry.visibility}
      noun="note"
      color="text.disabled"
    />
  ) : null;

  const promptPills =
    ratingPrompt || reactionPrompt || notesPrompt || standoutPrompt ? (
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        {ratingPrompt}
        {reactionPrompt}
        {notesPrompt}
        {standoutPrompt}
      </Stack>
    ) : null;

  // Leading gutter width: the full date column on desktop, just the status
  // icon on mobile (where the date sits in the content slot instead).
  const leadingWidth = isMobile ? CONTENT_INDENT : DATE_COLUMN;

  // Whatever the entry actually has, in fixed priority order. Any of the three
  // can be absent, so nothing may claim a slot by type — they fill from the
  // first available row downwards, which is what stops an entry from rendering
  // an empty row when (say) it has reactions but no rating.
  //
  // Each block carries its own slot metrics because a note's line box (20px)
  // is shorter than a row of controls (24px), and a note top-aligns and wraps
  // where the others centre on a single line.
  const contentBlocks = [
    ratingBlock && {
      key: "rating",
      node: ratingBlock,
      slotHeight: LINE_HEIGHT,
      contentAlign: "center",
    },
    reactionsBlock && {
      key: "reactions",
      node: reactionsBlock,
      slotHeight: LINE_HEIGHT,
      contentAlign: "center",
    },
    notesBlock && {
      key: "notes",
      node: notesBlock,
      slotHeight: NOTE_LINE_HEIGHT,
      contentAlign: "flex-start",
      trailing: visibilityButton,
    },
  ].filter(Boolean);

  // Desktop promotes the first block onto the date row, which would otherwise
  // sit empty. Mobile has no spare top line — the date occupies that content
  // slot — so every block goes to a row of its own below.
  const topBlock = isMobile ? null : contentBlocks[0];
  const belowBlocks = isMobile ? contentBlocks : contentBlocks.slice(1);

  // The date row (and so the season label beside it) tracks whichever block
  // it ended up carrying, so the date, more button and that block's first line
  // all centre on the same axis.
  const topSlotHeight = topBlock ? topBlock.slotHeight : LINE_HEIGHT;

  // Prompts only share the date row while it has nothing else in it. Once the
  // top slot carries a block they drop to a row of their own at the bottom, as
  // they always do on mobile — crowding them alongside content reads as clutter
  // and squeezes the block they sit next to.
  const promptsInTopRow = !isMobile && !topBlock;

  // Desktop's top slot falls back to the status label (e.g. "Queued") when it
  // would otherwise render empty — no content block promoted into it, and no
  // prompt pills claiming it either. Prompts always win the slot when present,
  // per promptsInTopRow above.
  const statusLabelBlock =
    promptsInTopRow && !promptPills ? (
      <Typography variant="body2" color="text.disabled">
        {statusOptions[status].label}
      </Typography>
    ) : null;

  return (
    <Box>
      <Box sx={{ display: "flex" }}>
        {seasonNumber != null && (
          // Fixed width keeps the right edge (and so every label) aligned in a
          // column. Matches the date's variant/weight, since season and date
          // are equally load-bearing when scanning a merged series+season list.
          <Box
            sx={{
              width: SEASON_GUTTER,
              flexShrink: 0,
              height: topSlotHeight, // track the date row so the label stays on its axis
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              // Leaves 8px to the status icon — the same gap the icon has to
              // the date text.
              pr: 1,
            }}
          >
            {seasonNumber === 0 ? (
              // Season 0 is the synthetic series-level entry; an icon reads
              // faster than another "S"-prefixed token beside real seasons.
              <VideoLibraryIcon
                fontSize="inherit"
                titleAccess="Series"
                sx={{ color: "text.primary" }}
              />
            ) : (
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ textAlign: "center" }}
              >
                <b>{`S${seasonNumber}`}</b>
              </Typography>
            )}
          </Box>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="column" spacing={ROW_GAP}>
            {/*
              Date row. On desktop it also carries the first content block and
              the prompt pills; mobile keeps the date in the content slot, so
              everything else drops to its own row below.
            */}
            <EntryRow
              leadingWidth={leadingWidth}
              slotHeight={topSlotHeight}
              contentAlign={topBlock ? topBlock.contentAlign : "center"}
              leading={isMobile ? statusIcon : dateBlock}
              trailing={moreButton}
            >
              {isMobile ? (
                dateButton
              ) : promptsInTopRow ? (
                // No block to hold, so the pills (or the status label
                // fallback) sit on their own at the start of the slot — no
                // growing spacer pushing them to the right.
                promptPills || statusLabelBlock
              ) : (
                <>
                  <Box
                    sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      display: "flex",
                      alignItems: topBlock.contentAlign,
                    }}
                  >
                    {topBlock.node}
                  </Box>
                  {/* The more button owns this row's trailing gutter, so a
                      promoted block's own trailing icon sits inline instead. */}
                  {topBlock.trailing}
                </>
              )}
            </EntryRow>

            {belowBlocks.map((block) => (
              <EntryRow
                key={block.key}
                leadingWidth={leadingWidth}
                slotHeight={block.slotHeight}
                contentAlign={block.contentAlign}
                trailing={block.trailing}
              >
                {block.node}
              </EntryRow>
            ))}

            {!promptsInTopRow && promptPills && (
              <EntryRow leadingWidth={leadingWidth}>{promptPills}</EntryRow>
            )}
          </Stack>
        </Box>
      </Box>

      <ListMenu
        anchorEl={moreMenuAnchorEl}
        onClose={() => setMoreMenuAnchorEl(null)}
        onSelect={handleMoreMenuSelect}
        options={moreMenuOptions}
        align="right"
        // Only on the series-level entry, where the gutter icon needs
        // explaining — seasons are self-evident from their "S1" label.
        footerText={
          seasonNumber === 0 ? (
            <>
              <VideoLibraryIcon
                sx={{ fontSize: "1rem", verticalAlign: "-0.2em", mr: 0.5 }}
              />
              This entry is for <br /> the whole series
            </>
          ) : null
        }
        TransitionProps={{
          onExited: () => {
            const action = pendingMenuActionRef.current;
            pendingMenuActionRef.current = null;

            if (action === "delete") {
              // Always asks first — the backend decides server-side whether
              // any confirmation is actually needed (see deleteEntryMutation)
              // rather than the client pre-judging it from the entry's fields.
              deleteEntryMutation.mutate({
                entryId: existingEntry.journal_id,
                confirm: false,
              });
            }
            if (action === "add_rating") setRatingRevealed(true);
            if (action === "edit_reactions") setActiveEditor("reactions");
            if (action === "edit_notes") setActiveEditor("notes");
            if (action === "edit_date") setDatePickerOpen(true);
            if (action === "share") {
              shareLink({
                url:
                  SHARE_ORIGIN +
                  buildContentPath({
                    contentType,
                    contentId,
                    title: existingEntry.content_title,
                  }) +
                  `?entry=${existingEntry.journal_id}`,
                title: existingEntry.content_title,
                copiedMessage:
                  "Link copied! Paste it anywhere to share your entry",
              });
            }
          },
        }}
      />

      <ConfirmActionDialog
        open={!!deleteConfirmation}
        onClose={() => setDeleteConfirmation(null)}
        title="Delete this entry?"
        message={<Typography>{getDeleteConfirmationMessage()}</Typography>}
        // Offered whenever the delete would take the finished status with it,
        // so the choice is always "keep it finished, or don't" rather than an
        // option that appears and disappears for reasons the user can't see.
        optionalAction={
          deleteConfirmation?.willClearStatus
            ? { label: "Keep as finished", onClick: keepAsFinished }
            : undefined
        }
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={() => {
          deleteEntryMutation.mutate({
            entryId: existingEntry.journal_id,
            confirm: true,
          });
        }}
      />

      <ReactionsEditorDialog
        open={activeEditor === "reactions"}
        reactions={editedEntry.reactions}
        contentType={contentType}
        onSave={({ reactions }) => {
          updateEditedEntry("reactions", reactions);
          saveField({ reactions });
          setActiveEditor(null);
        }}
        onClose={() => setActiveEditor(null)}
        isFullscreen={isMobile}
      />

      <NotesEditorDialog
        open={activeEditor === "notes"}
        notes={editedEntry.notes}
        visibility={editedEntry.visibility}
        status={editedEntry.status}
        onSave={({ notes, visibility }) => {
          updateEditedEntry("notes", notes);
          updateEditedEntry("visibility", visibility);
          saveField({ notes, visibility });
          setActiveEditor(null);
        }}
        onClose={() => setActiveEditor(null)}
        isFullscreen={isMobile}
      />
    </Box>
  );
}
